import { SubmissionError } from 'redux-form';
import { clone, forEach, get, isArray, isEmpty, isFunction, join, map, replace, result } from 'lodash';

import APIClient from '../../../../common/services/APIClient';
import dictionariesTypesEnum, { dictionariesSchema } from '../../../../common/constants/dictionaries';
import { ERROR_TYPE_VALIDATION } from '../../../../common/constants/errorTypes';
import { setMessageExportUnavailable } from '../../../../common/components/Alert/actions';
import { updateLabel } from '../../../../common/components/appConfig/actions';
import { fileDownload } from '../../../../common/services/fileUtils';

export const initDictionaryList = (name, dictionary) => ({
  type: 'INIT_DICTIONARY',
  dictionary,
  name,
});

export const removeDictionary = (dictionaryType) => ({
  type: 'REMOVE_DICTIONARY',
  dictionaryType,
});

export const setFetching = (isFetching) => ({
  type: 'SET_FETCHING',
  isFetching,
});

export const loadDictionary = (name, paginationObj) => (dispatch, getState) => {
  dispatch(setFetching(true));

  const {
    login: {
      user: { customer_id },
    },
  } = getState();

  const loadAndSetDictionary = (name, paginationOpts) => {
    dispatch(removeDictionary(name));

    const params = customer_id
      ? {
          ...paginationOpts,
          params: {
            ...paginationOpts.params,
            customerId: customer_id,
          },
        }
      : paginationOpts;

    return APIClient.get(`/dictionaries/${name}`, params)
      .then((resp) => {
        const payloads = result(resp, 'data.payloads', []);
        const size = result(resp, 'data.total_size', 0);

        dispatch(
          initDictionaryList(name, {
            dictionary: payloads,
            total_size: size,
            selected: [],
            importErrors: [],
            importRowsCount: 0,
            importing: false,
          }),
        );
        dispatch(setFetching(false));
      })
      .catch((err) => {
        dispatch(setFetching(false));
        console.error(err);
      });
  };

  const paginationOpts = {}; // TODO: remove later (for release only)

  if (paginationObj) {
    paginationOpts.page_token = 0;
    paginationOpts.page_size = 999;
  }

  const pagination = paginationObj ? { params: paginationOpts } : null;

  if (name === dictionariesTypesEnum.productVer) {
    loadAndSetDictionary(dictionariesTypesEnum.productType, pagination).then(() => {
      loadAndSetDictionary(name, pagination);
    });
  } else {
    loadAndSetDictionary(name, pagination);
  }
};

export const initPagination = (name, paginationObj) => ({
  type: 'SAVE_DICTIONARY_PAGINATION',
  name,
  paginationObj,
});

export const loadDictionaryForCustomer = (name, customerId) => {
  const paramsOpts = {
    page_token: 0,
    page_size: 999,
  };

  if (customerId) {
    paramsOpts.customerId = customerId;
  }

  return APIClient.get(`/dictionaries/${name}`, { params: paramsOpts })
    .then((resp) => get(resp, 'data.payloads', []))
    .catch((err) => {
      console.error(err);
    });
};

export const setPagination = (name, paginationObj) => (dispatch) => {
  dispatch(initPagination(name, paginationObj));
  dispatch(loadDictionary(name, paginationObj));
};

const validationErrorProcessor = (error) => {
  const errorType = get(error, 'response.data.error_type');

  if (errorType === ERROR_TYPE_VALIDATION) {
    const errors = get(error, 'response.data.errors');

    if (!isEmpty(errors)) {
      const validationError = {};

      forEach(errors, (e) => {
        let { message } = e;

        forEach(dictionariesSchema, (dictionary) => {
          message = replace(message, dictionary.name, `'${dictionary.label}'`);
        });
        validationError[e.field] = message;
      });
      throw new SubmissionError(validationError);
    } else {
      throw new SubmissionError('Unhandled server validation error.');
    }
  }
};

const baseErrorProcessor = (error, defaultMessage) => {
  const errors = get(error, 'response.data.errors');

  if (!isEmpty(errors)) {
    const message = join(map(errors, 'message'), ';');

    throw new SubmissionError({ _error: message });
  } else {
    throw new SubmissionError({ _error: defaultMessage });
  }
};

const errorHandlers = {
  400: {
    handler: validationErrorProcessor,
  },
  404: {
    handler: baseErrorProcessor,
    message: 'This dictionary is not exists. Please refresh the page.',
  },
  500: {
    handler: baseErrorProcessor,
    message: 'This dictionary is not responsible now. Please refresh the page.',
  },
};

export const handleCreateEditDictionaryError = (error) => {
  console.log(error);
  const responseStatus = get(error, 'response.status');
  const errorHandler = errorHandlers[responseStatus];

  errorHandler && isFunction(errorHandler.handler)
    ? errorHandler.handler(error, errorHandler.message)
    : baseErrorProcessor(error, 'Unhandled server error.');
};

const newEditFieldToLabels = ({ key, value }) => ({ [key]: value });

export const addItem = (name, item) => ({
  type: 'ADD_ITEM',
  name,
  item,
});

export const createDictionaryValue = (name, obj) => (dispatch) => {
  const newBody = {
    value: obj.value.name || obj.value,
    type: name,
    status: obj.status,
    parent_id: get(obj, 'parentId'),
    key: obj.key,
    code: obj.value.code,
  };

  return APIClient.post('/dictionaries', newBody)
    .then((resp) => {
      dispatch(addItem(name, resp.data));
      if (name === dictionariesTypesEnum.fieldNaming) {
        dispatch(updateLabel(newEditFieldToLabels(resp.data)));
      }
    })
    .catch(handleCreateEditDictionaryError);
};

export const editItem = (uuid, item) => ({
  type: 'EDIT_ITEM',
  uuid,
  item,
});

export const editDictionaryValue = (uuid, modifiedRecord) => (dispatch) => {
  const recordToSave = clone(modifiedRecord);

  recordToSave.parent_id = get(recordToSave, 'parentId', '');
  recordToSave.code = get(recordToSave, 'value.code') || get(recordToSave, 'code');
  recordToSave.value = get(recordToSave, 'value.name') || recordToSave.value;

  return APIClient.put(`/dictionaries/${uuid}`, recordToSave)
    .then((resp) => {
      dispatch(editItem(uuid, resp.data));
      if (recordToSave.type === dictionariesTypesEnum.fieldNaming) {
        dispatch(updateLabel(newEditFieldToLabels(resp.data)));
      }
    })
    .catch(handleCreateEditDictionaryError);
};

export const storeDictionaries = (dictionaries) => ({
  type: 'STORE_DICTIONARIES',
  dictionaries,
});

export const loadAllDictionaries =
  (filter = {}) =>
  (dispatch) => {
    APIClient.post('/dictionaries/view', { filter_by: filter }).then((resp) => {
      dispatch(storeDictionaries(resp.data.dictionaries));
    });
  };

export const exportDictionary =
  (dictionaryType, uuids = [], sorting, customFilter = false) =>
  (dispatch) => {
    let tableSorting;

    if (isArray(sorting)) {
      tableSorting = sorting[0] || { id: 'value', desc: false };
    } else {
      tableSorting = sorting || { id: 'value', desc: false };
    }

    const params = {
      sort_by: tableSorting.id,
      sort_order: tableSorting.desc ? 'DESC' : 'ASC',
    };

    const payload = customFilter
      ? uuids
      : {
          filter_by: { uuids },
        };

    return APIClient.post(`dictionaries/${dictionaryType}/export`, payload, { params })
      .then(({ data, headers }) => {
        fileDownload(data, `exported-${dictionaryType.toLowerCase()}.csv`, headers['content-type']);
      })
      .catch((err) => {
        if (err.response && err.response.status === 500) {
          dispatch(setMessageExportUnavailable());
        }
        console.error(err);
      });
  };

export const toggleSelectedDictionary = (dictionaryType, uuid) => ({
  type: 'TOGGLE_SELECTED_DICTIONARY',
  dictionaryType,
  uuid,
});

export const toggleSelectedAllDictionaries = (dictionaryType) => ({
  type: 'TOGGLE_SELECTED_ALL_DICTIONARIES',
  dictionaryType,
});

export const deselectAllDictionaries = (dictionaryType) => ({
  type: 'DESELECT_ALL_DICTIONARIES',
  dictionaryType,
});
